import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Trophy, Share2, DollarSign, Sparkles, ArrowRight, Loader2,
  Link2, CheckCircle2, TrendingUp,
} from "lucide-react";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const TIERS = [
  {
    name: "Bronze",
    refs: "0 – 9 orders",
    pay: "10",
    accent: "#E9AE7F",
    ring: "border-[#B8804D]/40",
  },
  {
    name: "Silver",
    refs: "10 – 24 orders",
    pay: "20",
    accent: "#DAE0E5",
    ring: "border-[#B8BFC7]/40",
    featured: true,
  },
  {
    name: "Gold",
    refs: "25+ orders",
    pay: "30",
    accent: "#F5C542",
    ring: "border-[#F5C542]/40",
  },
];

const STEPS = [
  { icon: Trophy,     title: "Enroll in seconds",  body: "Sign in and click ‘Join’ from your account. We generate a 6-character code you can start using instantly." },
  { icon: Share2,     title: "Share your link",    body: "Post your unique link anywhere — X, Reddit, private groups, a blog, a QR code on a business card." },
  { icon: DollarSign, title: "Get paid per order", body: "Every completed order attributed to your link earns you a flat commission. Cash out any time via e-Transfer or PayPal." },
];

export default function Partners() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const primaryCta = async () => {
    if (!user || !user.user_id) {
      // Not logged in → route to sign-up and come back
      navigate("/register?next=/account?tab=partnership");
      return;
    }
    // Logged in → enroll immediately, then jump to dashboard
    setBusy(true);
    try {
      await api.post("/partnership/join");
      toast.success("You're in — welcome to the partnership program!");
      navigate("/account?tab=partnership");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not enroll");
    } finally {
      setBusy(false);
    }
  };

  const ctaLabel = !user || !user.user_id ? "Sign up to join" : "Join now — get your code";

  return (
    <div className="pt-20 min-h-screen flex flex-col bg-[#0F0F10] text-[#E5E5E5]">
      <SEO
        title="Partnership Program"
        description="Earn $10, $20 or $30 per referred order. Simple, transparent 3-tier affiliate program with e-Transfer and PayPal payouts. Turn your audience into income."
        canonicalPath="/partners"
        image="https://endag1.com/og-partners.png"
      />

      {/* HERO */}
      <section className="relative mx-auto max-w-[1400px] w-full px-6 sm:px-10 pt-14 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <Reveal className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-[#4ADE80] mb-6">
              <Sparkles className="h-3.5 w-3.5" /> Partnership Program · Now Open
            </div>
            <h1 className="font-display font-black text-5xl sm:text-6xl md:text-7xl leading-[0.95] tracking-tighter uppercase text-[#F5F5F5]">
              Earn every time<br />
              <span className="text-[#4ADE80]">someone shops</span><br />
              through your link.
            </h1>
            <p className="mt-8 max-w-xl text-lg text-[#A8A8A8] leading-relaxed">
              Simple, flat-dollar payouts. Three tiers. Thirty-day cookie window. No approvals, no
              waiting — just share, earn, and cash out.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Button
                data-testid="partners-hero-cta"
                onClick={primaryCta}
                disabled={busy}
                className="bg-[#4ADE80] text-[#0F0F10] hover:bg-[#6FE998] px-8 py-6 text-sm uppercase tracking-[0.16em]"
              >
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trophy className="h-4 w-4 mr-2" />}
                {ctaLabel}
              </Button>
              <a
                href="#how"
                className="btn-press inline-flex items-center gap-2 border border-white/20 text-[#F5F5F5] px-6 py-3 text-[11px] uppercase tracking-[0.16em] hover:border-[#4ADE80] hover:text-[#4ADE80] transition-colors"
              >
                How it works <ArrowRight className="h-3 w-3" />
              </a>
            </div>
            <div className="mt-10 flex items-center gap-8 text-xs text-[#A8A8A8]">
              <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#4ADE80]" /> No caps on earnings</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#4ADE80]" /> 30-day attribution</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#4ADE80]" /> Weekly payouts</div>
            </div>
          </Reveal>

          {/* Right visual: floating card mockup */}
          <Reveal delay={0.2} className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-8 bg-[#4ADE80]/10 blur-3xl rounded-full" />
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 1.2 }}
                className="relative border border-white/10 bg-gradient-to-br from-[#171717] to-[#0F0F10] p-8 shadow-2xl"
              >
                <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.24em] text-[#A8A8A8]">
                  <span>EndaG1 Partner Card</span>
                  <span className="text-[#4ADE80]">Bronze · $10/order</span>
                </div>
                <div className="mt-6 text-xs uppercase tracking-[0.16em] text-[#A8A8A8]">Your code</div>
                <div className="font-mono text-5xl text-[#4ADE80] tracking-widest mt-2">R7X4K2</div>
                <div className="mt-6 pt-4 border-t border-white/10 flex items-center gap-3 text-xs">
                  <Link2 className="h-4 w-4 text-[#A8A8A8]" />
                  <span className="font-mono text-[#F5F5F5]">endag1.com/?ref=R7X4K2</span>
                </div>
                <div className="mt-8 grid grid-cols-3 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-[#A8A8A8]">Refs</div>
                    <div className="font-display text-2xl text-[#F5F5F5] mt-1">14</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-[#A8A8A8]">Earned</div>
                    <div className="font-display text-2xl text-[#4ADE80] mt-1">$180</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-[#A8A8A8]">Pending</div>
                    <div className="font-display text-2xl text-[#F5F5F5] mt-1">$60</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TIERS */}
      <section id="tiers" className="mx-auto max-w-[1400px] w-full px-6 sm:px-10 py-24 border-t border-white/5">
        <Reveal>
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#4ADE80]">Payout Tiers</div>
          <h2 className="font-display text-4xl md:text-5xl text-[#F5F5F5] mt-3 tracking-tight leading-[1.05]">
            The more you refer,<br />the more you make per order.
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-14">
          {TIERS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.08}>
              <div
                className={`relative border ${t.ring} bg-[#171717] p-8 h-full flex flex-col ${
                  t.featured ? "shadow-[0_0_60px_-20px_rgba(74,222,128,0.4)]" : ""
                }`}
              >
                {t.featured && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#4ADE80] text-[#0F0F10] text-[9px] uppercase tracking-[0.24em] px-3 py-1 font-semibold">
                    Most Popular
                  </span>
                )}
                <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: t.accent }}>
                  Tier · {t.name}
                </div>
                <div className="mt-6 font-display text-6xl text-[#F5F5F5]">
                  <span className="text-3xl align-top">$</span>{t.pay}
                  <span className="text-sm text-[#A8A8A8] font-body normal-case tracking-normal ml-1">/order</span>
                </div>
                <div className="mt-4 text-sm text-[#A8A8A8]">{t.refs}</div>
                <ul className="mt-8 space-y-3 text-sm text-[#A8A8A8] flex-1">
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[#4ADE80] mt-0.5 shrink-0" /> Flat-dollar payout per completed order</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[#4ADE80] mt-0.5 shrink-0" /> Auto-advances at threshold — no reapplying</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[#4ADE80] mt-0.5 shrink-0" /> e-Transfer or PayPal payouts</li>
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="mx-auto max-w-[1400px] w-full px-6 sm:px-10 py-24 border-t border-white/5">
        <Reveal>
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#4ADE80]">How it works</div>
          <h2 className="font-display text-4xl md:text-5xl text-[#F5F5F5] mt-3 tracking-tight leading-[1.05]">
            Three steps, zero paperwork.
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
          {STEPS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.1}>
              <div className="relative border border-white/10 bg-[#171717] p-8 h-full">
                <div className="absolute -top-4 left-8 bg-[#4ADE80] text-[#0F0F10] font-mono text-sm w-8 h-8 flex items-center justify-center">
                  {i + 1}
                </div>
                <s.icon className="h-6 w-6 text-[#4ADE80] mt-2" />
                <h3 className="mt-5 font-display text-2xl text-[#F5F5F5]">{s.title}</h3>
                <p className="mt-3 text-sm text-[#A8A8A8] leading-relaxed">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="mx-auto max-w-[1400px] w-full px-6 sm:px-10 py-24 border-t border-white/5">
        <Reveal>
          <div className="border border-[#4ADE80]/30 bg-gradient-to-br from-[#4ADE80]/10 to-transparent p-10 md:p-16 text-center">
            <TrendingUp className="h-8 w-8 text-[#4ADE80] mx-auto" />
            <h2 className="mt-6 font-display font-black text-4xl md:text-6xl uppercase tracking-tighter leading-[0.95]">
              Start earning<br /><span className="text-[#4ADE80]">this week.</span>
            </h2>
            <p className="mt-6 max-w-lg mx-auto text-[#A8A8A8]">
              Your code is generated in seconds. Post it once, and let the 30-day cookie window do the rest.
            </p>
            <Button
              data-testid="partners-footer-cta"
              onClick={primaryCta}
              disabled={busy}
              className="mt-8 bg-[#4ADE80] text-[#0F0F10] hover:bg-[#6FE998] px-10 py-6 text-sm uppercase tracking-[0.16em]"
            >
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trophy className="h-4 w-4 mr-2" />}
              {ctaLabel}
            </Button>
            {(!user || !user.user_id) && (
              <div className="mt-5 text-xs text-[#A8A8A8]">
                Already have an account? <Link to="/login?next=/partners" className="text-[#4ADE80] hover:underline">Sign in</Link>
              </div>
            )}
          </div>
        </Reveal>
      </section>

      <Footer />
    </div>
  );
}
